import { collectionService } from './collectionService';
import { apiRequestService } from './apiRequestService';
import { workspaceService } from './workspaceService';

/**
 * Client-side accept for an ENCRYPTED snapshot share landing in a CLOUD
 * destination.
 *
 * `POST /share/:token/accept` builds the forked rows on the server by walking
 * `share_links.payload`. Once that payload is an envelope (see
 * `utils/shareEnvelope.js`) the server can no longer read the request fields —
 * it would happily create a collection of empty requests and report success,
 * which is the silent-corruption failure this whole feature exists to avoid.
 *
 * So an encrypted share is accepted the way LOCAL destinations have always been
 * accepted (`ShareAcceptModal`: fetch `/share/:token/data`, decrypt in the
 * client, create the rows): the same shape, pointed at the ordinary cloud APIs
 * instead of the local snapshot store. Nothing here is a new protocol — every
 * call below is one the app already makes elsewhere.
 *
 * What is deliberately NOT reimplemented: plan limits, authorization and the
 * per-collection request cap all still live server-side on `/collections/import`,
 * `/workspaces` and `/api-requests`, so a free account hits the same 402 it
 * would have hit through the accept endpoint. This is a different route to the
 * same guards, not a way around them.
 *
 * Known difference from the server accept path, stated rather than hidden: the
 * server does its fork in ONE transaction, and this cannot. A failure partway
 * through a workspace accept leaves the workspace created and some collections
 * missing. The user sees the error and the half-made workspace; nothing is
 * silently wrong. Making it atomic needs a server endpoint that takes a
 * decrypted tree, which is a bigger change than this one.
 */

const AUTO_COLLECTION_NAME = 'Shared Requests';

/** Strip ids that belong to the SENDER's rows — never re-used on the fork. */
function withoutIds(obj) {
  const { id, uid, collection_id: _c, workspace_id: _w, folder_id: _f, ...rest } = obj || {};
  return rest;
}

async function createWorkspaceFor({ name, description, teamId }) {
  const payload = { name, description: description || '' };
  // The server derives `type`/`visibility` from team_id — see WorkspaceModal.
  if (teamId) payload.team_id = teamId;
  const ws = await workspaceService.createWorkspace(payload);
  if (!ws?.id) throw new Error('Failed to create the destination workspace');
  return ws;
}

/** Find-or-create the destination collection for a single-request share. */
async function resolveRequestCollection(workspaceId, { collectionId, newCollectionName }) {
  if (collectionId) return collectionId;
  if (newCollectionName) {
    const created = await collectionService.createCollection({
      name: newCollectionName,
      workspace_id: workspaceId,
    });
    return created.id;
  }
  // "Shared Requests (automatic)" — mirrors the server's fallback destination,
  // including reusing the existing one rather than making a second.
  const raw = await collectionService.getCollections(workspaceId);
  const existing = (Array.isArray(raw) ? raw : raw?.collections || []).find(
    (c) => c?.name === AUTO_COLLECTION_NAME
  );
  if (existing?.id) return existing.id;
  const created = await collectionService.createCollection({
    name: AUTO_COLLECTION_NAME,
    workspace_id: workspaceId,
  });
  return created.id;
}

/**
 * Create the cloud rows for one decrypted share.
 *
 * `data` is the plaintext sub-map from `shareService.fetchShareData` after
 * `decryptSnapshotData` — i.e. exactly the tree the server would have walked.
 */
export async function acceptDecryptedShareIntoCloud({
  type,
  data,
  workspaceId = null,
  newWorkspaceName = null,
  newWorkspaceTeamId = null,
  collectionId = null,
  newCollectionName = null,
}) {
  if (!data) throw new Error('This share has no content to save');

  if (type === 'workspace') {
    // A workspace share always forks into a brand-new workspace.
    const ws = await createWorkspaceFor({
      name: newWorkspaceName || data.name || 'Shared Workspace',
      description: data.description,
      teamId: newWorkspaceTeamId,
    });
    const collections = (data.collections || []).map(withoutIds);
    if (collections.length > 0) await collectionService.importCollections(ws.id, collections);
    // The variables the sender chose to include: without them every
    // {{placeholder}} in the fork is broken. Same keys the server merges.
    const s = data.settings || {};
    if (s.globals || s.workspace_globals || s.environments) {
      await workspaceService.updateVariableScopes(ws.id, {
        globals: s.globals || [],
        workspace_globals: s.workspace_globals || [],
        environments: s.environments || [],
      });
    }
    return { workspaceId: ws.id };
  }

  const targetWorkspaceId = workspaceId
    ? workspaceId
    : (
        await createWorkspaceFor({
          name: newWorkspaceName || data.name || 'Shared Workspace',
          description: '',
          teamId: newWorkspaceTeamId,
        })
      ).id;

  if (type === 'collection') {
    await collectionService.importCollections(targetWorkspaceId, [withoutIds(data)]);
    return { workspaceId: targetWorkspaceId };
  }

  if (type === 'request') {
    const destination = await resolveRequestCollection(targetWorkspaceId, {
      // A brand-new workspace has no collections to pick from, so the picker's
      // choice cannot apply to it.
      collectionId: workspaceId ? collectionId : null,
      newCollectionName,
    });
    await apiRequestService.createRequest({
      ...withoutIds(data),
      collection_id: destination,
    });
    return { workspaceId: targetWorkspaceId };
  }

  throw new Error(`Unsupported share type: ${type}`);
}
